/**
 * Custom operational RUM signals. Product analytics stays in Mixpanel and the
 * native analytics bridge. See `docs/telemetry.md` for monitor guidance.
 */

import { trackDatadogAction } from "./datadog";

export const RUM_ACTIONS = {
  // Native WebView bridge
  NATIVE_BRIDGE_ERROR: "native.bridge_error",
  NATIVE_BRIDGE_RETRIES_EXHAUSTED: "native.bridge_retries_exhausted",

  // Patient profile
  PATIENT_PROFILE_LOAD_ERROR: "patient.profile_load_error",

  // Authentication lifecycle
  AUTH_SESSION_LOST: "auth.session_lost",
  AUTH_LOGOUT: "auth.logout",
  AUTH_LOGIN_COLD: "auth.login_cold",
  AUTH_RECONCILE_CLEAR: "auth.reconcile_clear",
  AUTH_REVALIDATE_CHANGED: "auth.revalidate_changed",
} as const;

// Native bridge

/**
 * Reports a native bridge that threw. `bridge` names the handler, never the
 * payload — those carry medication and screen detail.
 */
export function trackRumNativeBridgeError(context: { bridge: string }): void {
  trackDatadogAction(RUM_ACTIONS.NATIVE_BRIDGE_ERROR, context);
}

export function trackRumNativeBridgeRetriesExhausted(context: {
  logPrefix: string;
  attempts: number;
}): void {
  trackDatadogAction(RUM_ACTIONS.NATIVE_BRIDGE_RETRIES_EXHAUSTED, context);
}

// Authentication

/** Why a session was deliberately torn down, as opposed to lost. */
export type AuthLogoutReason = "not_enrolled" | "user_initiated";

// Module state survives SPA navigation but resets on a genuine page reload.
// sessionStorage cannot be used because deliberate teardown clears it.
let hasHeldCredentials = false;
let activeLogoutReason: AuthLogoutReason | undefined;

/** Called whenever credentials are observed, so a later login view can be
 * recognised as a session that was lost rather than never established. */
export function noteAuthenticated(): void {
  hasHeldCredentials = true;
  activeLogoutReason = undefined;
}

/** Records a deliberate teardown and suppresses login remounts until re-auth. */
export function trackRumLogout(reason: AuthLogoutReason): void {
  activeLogoutReason = reason;
  trackDatadogAction(RUM_ACTIONS.AUTH_LOGOUT, { reason });
}

/**
 * Classifies a settled login screen. Call only once the login *form* is
 * showing — while auto-login is still in flight the page renders a loader, and
 * counting that would report every successful WebView launch as a failure.
 */
export function trackRumLoginView(context: {
  platform?: string;
  hasAutoLoginCookie: boolean;
}): void {
  const { platform, hasAutoLoginCookie } = context;

  if (activeLogoutReason) {
    return;
  }

  // A surviving auto-login cookie means the native app still considers this
  // user signed in, so reaching the form is a lost session even on first load.
  const name =
    hasHeldCredentials || hasAutoLoginCookie
      ? RUM_ACTIONS.AUTH_SESSION_LOST
      : RUM_ACTIONS.AUTH_LOGIN_COLD;

  trackDatadogAction(name, {
    hasAutoLoginCookie,
    ...(platform !== undefined && { platform }),
  });
}

export function trackRumAuthReconcileClear(context: {
  cookieName: string;
}): void {
  trackDatadogAction(RUM_ACTIONS.AUTH_RECONCILE_CLEAR, context);
}

export function trackRumAuthRevalidateChanged(): void {
  trackDatadogAction(RUM_ACTIONS.AUTH_REVALIDATE_CHANGED);
}

/** Test seam — module state would otherwise leak between cases. */
export function resetAuthTelemetryState(): void {
  hasHeldCredentials = false;
  activeLogoutReason = undefined;
}
